import React, { useEffect, useState } from "react";
import "./dashboard-layout.scss";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthHelper from "../../../features/authentication/helpers/auth-helper/auth-helper";
import QRViewer from "../qr-viewer/qr-viewer";
import OutsideClickHandler from "react-outside-click-handler";
import { Tooltip } from "react-tooltip";
import OrganizationApi from "../../../api/organization-api";
import { UserStoreActions } from "../../../redux/user-store";
import Popup from "./components/popup/popupComponent";
import "@theme-toggles/react/css/Within.css"
import { Within } from "@theme-toggles/react";
import "./design-system/design-system.scss";
import Input from "./components/inputFields/inputComponent";
import { fetchOrgProperties, isSidebarLinkEnabled } from "../../../features/organization-properties/organization-properties-helper";
import TermsModal from "../../../features/terms-and-conditions/components/terms-modal/terms-modal";

const DashboardLayout = ({ children }) => {
  const { userInfo } = useSelector((state) => state.userStore);
  const dispatch = useDispatch();
  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );
  const themeStorageKey = React.useMemo(() => {
    return organizationId ? `dashboardTheme:${organizationId}` : "dashboardTheme";
  }, [organizationId]);
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgFetched, setOrgFetched] = useState(false);
  const [canManageUsers, setCanManageUsers] = React.useState(false);
  const [canAccessDeveloperTools, setCanAccessDeveloperTools] = React.useState(false);
  const isB2C = userInfo?.role === "b2c";

  const [orgSidebarLinks, setOrgSidebarLinks] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const _logout = (e) => {
    e.preventDefault();
    AuthHelper.logout();
  };

  useEffect(() => {
    if (userInfo?.role === "admin") {
      setCanManageUsers(true);
      setCanAccessDeveloperTools(true);
    }
  }, [userInfo]);

  useEffect(() => {
    let isMounted = true;
    const fetchOrg = async () => {
      if (!organizationId) {
        setOrgDetails(null);
        setOrgFetched(true);
        return;
      }
      try {
        setOrgLoading(true);
        const response = await OrganizationApi.private.fetchOrganization(organizationId);
        const data = response?.data?.data;
        if (isMounted) setOrgDetails(data || null);
      } catch (err) {
        console.error("Error fetching organization details:", err);
        if (isMounted) setOrgDetails(null);
      } finally {
        if (isMounted) {
          setOrgLoading(false);
          setOrgFetched(true);
        }
      }
    };
    fetchOrg();
    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  useEffect(() => {
    let isMounted = true;
    const loadOrgProperties = async () => {
      if (!organizationId) return;
      try {
        const props = await fetchOrgProperties(organizationId);
        if (isMounted) setOrgSidebarLinks(props?.sidebarLinks ?? null);
      } catch {
        // Silently fail — default is to show all links
      }
    };
    loadOrgProperties();
    return () => { isMounted = false; };
  }, [organizationId]);

  const hasValidServiceAccess = React.useMemo(() => {
    const value = orgDetails?.hasServiceAccessUntil;
    if (!value) return false;
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return false;
    return ts > Date.now();
  }, [orgDetails]);

  const hasPaymentInfo = React.useMemo(() => {
    return Boolean(orgDetails?.paymentInfo?.customerId);
  }, [orgDetails]);

  const isServiceAccessRestricted = orgFetched && !orgLoading && !hasValidServiceAccess;
  const isServiceAccessKnown = orgFetched && !orgLoading;
  const canShowServiceAccessGatedLinks = isServiceAccessKnown && !isServiceAccessRestricted;

  useEffect(() => {
    if (!orgFetched) return;
    if (orgLoading) return;

    dispatch(
      UserStoreActions.setServiceAccessStatus({
        fetched: true,
        hasValid: hasValidServiceAccess,
      })
    );
  }, [dispatch, hasValidServiceAccess, orgFetched, orgLoading]);

  useEffect(() => {
    if (!isServiceAccessRestricted) return;

    const allowedPaths = ["/settings", "/developer-access"];
    const isAllowed = allowedPaths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );

    if (!isAllowed) {
      navigate("/settings", { replace: true });
    }
  }, [isServiceAccessRestricted, location.pathname, navigate]);

  const [profileDropdownShow, setProfileDropdownShow] = useState(false);
  const organizationName = orgDetails?.name || (orgLoading ? "Loading..." : "Your Organization");
  const orgInitial = orgDetails?.name
    ? (orgDetails.name.trim()[0] || "?").toUpperCase()
    : (orgLoading ? "…" : "?");

  const [toggleSidebar, setToggleSidebar] = useState(false);

  const [showOrgSettingPopup, setShowOrgSettingPopup] = useState(false);

  const [presenceOpen, setPresenceOpen] = useState(false);
  const [logoutConfirming, setLogoutConfirming] = useState(false);

  const closePresence = () => {
    setPresenceOpen(false);
    setLogoutConfirming(false);
  };

  const [isDarkMode, setIsDarkMode] = useState(false);

  const [userThemePreference, setUserThemePreference] = useState(null);

  // Terms & Conditions modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const isTermsAccepted = Boolean(orgDetails?.termsAcceptance?.accepted);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const value = window.localStorage.getItem(themeStorageKey);
    if (value !== "dark" && value !== "light") {
      setUserThemePreference(null);
      return;
    }
    setUserThemePreference(value);
  }, [themeStorageKey]);

  const isOrgThemeKnown = orgFetched && !orgLoading;

  useEffect(() => {
    // B2C users are always in "All" mode (isDarkMode = false)
    if (isB2C) {
      setIsDarkMode(false);
      return;
    }

    if (userThemePreference) {
      setIsDarkMode(userThemePreference === "dark");
      return;
    }

    if (isOrgThemeKnown) {
      setIsDarkMode(Boolean(hasPaymentInfo));
      return;
    }

    setIsDarkMode(false);
  }, [hasPaymentInfo, isB2C, isOrgThemeKnown, userThemePreference]);

  // Sync body attribute
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.setAttribute(
        "theme",
        isDarkMode ? "dark" : "light"
      );
    }
  }, [isDarkMode]);

  // Auto-show terms modal if not yet accepted
  useEffect(() => {
    if (orgFetched && !orgLoading && orgDetails && !orgDetails.termsAcceptance?.accepted) {
      setShowTermsModal(true);
    }
  }, [orgFetched, orgLoading, orgDetails]);

  const handleAcceptTerms = async () => {
    try {
      setIsAcceptingTerms(true);
      await OrganizationApi.private.acceptTerms(organizationId);
      // Re-fetch org details to reflect accepted state
      const response = await OrganizationApi.private.fetchOrganization(organizationId);
      const data = response?.data?.data;
      setOrgDetails(data || null);
      setShowTermsModal(false);
    } catch (err) {
      console.error("Error accepting terms:", err);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <OutsideClickHandler onOutsideClick={() => setToggleSidebar(false)}>
        <div className={"left-sidebar " + (toggleSidebar ? "show" : "")}>
          {/* LOGO */}
          <div className="brand">
            <a href="index.html" className="logo">
              <span>
                <img
                  src="assets/images/small_icon_withBg.png"
                  alt="logo-small"
                  className="logo-sm"
                />
              </span>
            </a>
          </div>
          <Tooltip id="TooltipConnectContacts" />
          <Tooltip id="TooltipManageGroups" />
          <Tooltip id="TooltipMessages" />
          <Tooltip id="TooltipTranscripts" />
          <Tooltip id="TooltipGroupMessaging" />
          <Tooltip id="TooltipFeatures" />
          <Tooltip id="TooltipUserManagement" />
          <Tooltip id="TooltipContactList" />
          <Tooltip id="TooltipDeveloperAccess" />
          <Tooltip id="TooltipExternalTemplates" />
          <Tooltip id="TooltipIntegrations" />
          <Tooltip id="TooltipTerms" />
          <div className="menu-content" data-simplebar>
            <div className="menu-body navbar-vertical">
              <div
                className="collapse navbar-collapse tab-content"
                id="sidebarCollapse"
              >
                <ul
                  className="navbar-nav tab-pane active"
                  id="Main"
                  role="tabpanel"
                >
                  {canShowServiceAccessGatedLinks && !isDarkMode && isSidebarLinkEnabled(orgSidebarLinks, "link1") && (
                    <Link
                      to="/manage-groups"
                      data-tooltip-id="TooltipManageGroups"
                      data-tooltip-content="Manage Groups"
                      className={`nav-link position-relative ${
                        location.pathname === "/manage-groups" ? "active" : ""
                      }`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M17 20C17 18.3431 14.7614 17 12 17C9.23858 17 7 18.3431 7 20M21 17.0004C21 15.7702 19.7659 14.7129 18 14.25M3 17.0004C3 15.7702 4.2341 14.7129 6 14.25M18 10.2361C18.6137 9.68679 19 8.8885 19 8C19 6.34315 17.6569 5 16 5C15.2316 5 14.5308 5.28885 14 5.76389M6 10.2361C5.38625 9.68679 5 8.8885 5 8C5 6.34315 6.34315 5 8 5C8.76835 5 9.46924 5.28885 10 5.76389M12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11C15 12.6569 13.6569 14 12 14Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                      <span>Manage Groups</span>
                    </Link>
                  )}

                  {canShowServiceAccessGatedLinks && !isB2C && !isDarkMode && isSidebarLinkEnabled(orgSidebarLinks, "link2") && (
                    <Link
                      className={`nav-link position-relative ${
                        location.pathname === "/transcript-chats" ? "active" : ""
                      }`}
                      to="/transcript-chats"
                      // title="Messages"
                      data-tooltip-id="TooltipTranscripts"
                      data-tooltip-content="Transcripts"
                    >
                      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M0 0h48v48H0z" fill="none"></path> <g id="Shopicon"> <path d="M4,44l8-8h20c2.2,0,4-1.8,4-4v-4l8,8V8c0-2.2-1.8-4-4-4H16c-2.2,0-4,1.8-4,4v4H8c-2.2,0-4,1.8-4,4V44z M16,8h24v18.343 L37.657,24H16V8z M8,16h4v8c0,2.2,1.8,4,4,4h16v4H10.343L8,34.343V16z"></path> </g> </g></svg>
                      <span>Transcripts</span>
                      <div class="position-absolute top-0 start-100 translate-middle p-1 bg-danger rounded-circle">
                        <div class="visually-hidden">New alerts</div>
                      </div>
                    </Link>
                  )}

                  {canShowServiceAccessGatedLinks && !isB2C && !isDarkMode && isSidebarLinkEnabled(orgSidebarLinks, "link3") && (
                  <Link
                    className={`nav-link position-relative ${
                      location.pathname === "/whatsapp-chats" ? "active" : ""
                    }`}
                    to="/whatsapp-chats"
                    // title="Messages"
                    data-tooltip-id="TooltipMessages"
                    data-tooltip-content="Messages"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 9H17M7 13H12M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>Messages</span>
                    <div class="position-absolute top-0 start-100 translate-middle p-1 bg-danger rounded-circle">
                      <div class="visually-hidden">New alerts</div>
                    </div>
                  </Link>
                  )}
                 
                  {canShowServiceAccessGatedLinks && !isB2C && !isDarkMode && isSidebarLinkEnabled(orgSidebarLinks, "link4") && (
                  <Link
                    className={`nav-link position-relative ${
                      location.pathname === "/whatsapp-groups" ? "active" : ""
                    }`}
                    to="/whatsapp-groups"
                    data-tooltip-id="TooltipGroupMessaging"
                    data-tooltip-content="Group messaging"
                  >
                    <svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg"> <circle cx="13" cy="6.5" r="5" stroke="black" stroke-width="3"/> <circle cx="13" cy="30.5" r="5" stroke="black" stroke-width="3"/> <circle cx="36.5" cy="6.5" r="5" stroke="black" stroke-width="3"/> <circle cx="36.5" cy="30.5" r="5" stroke="black" stroke-width="3"/> <path d="M24 24C24 17.9249 19.0751 13 13 13C6.92487 13 2 17.9249 2 24" stroke="black" stroke-width="3"/> <path d="M24 48C24 41.9249 19.0751 37 13 37C6.92487 37 2 41.9249 2 48" stroke="black" stroke-width="3"/> <path d="M47.5 24C47.5 17.9249 42.5751 13 36.5 13C30.4249 13 25.5 17.9249 25.5 24" stroke="black" stroke-width="3"/> <path d="M47.5 48C47.5 41.9249 42.5751 37 36.5 37C30.4249 37 25.5 41.9249 25.5 48" stroke="black" stroke-width="3"/> </svg>
                    <span>Group messaging</span>
                  </Link>
                  )}

                
                {/* Contact List - visible for B2C */}
                {
                  canShowServiceAccessGatedLinks && isSidebarLinkEnabled(orgSidebarLinks, "link5") && (
                    <Link
                  className={`nav-link ${
                    location.pathname === "/manage-contacts" ? "active" : ""
                  }`}
                  to="/manage-contacts"
                  // title="Contact List"
                  data-tooltip-id="TooltipContactList"
                  data-tooltip-content="Contact List"
                >
                  <svg fill="#000000" viewBox="0 0 16 16" id="contacts-16px" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path id="Path_131" data-name="Path 131" d="M33,6a2,2,0,1,0-2,2A2,2,0,0,0,33,6ZM31,7a1,1,0,1,1,1-1A1,1,0,0,1,31,7Zm7.5-1H38V3.5A3.5,3.5,0,0,0,34.5,0h-10a.5.5,0,0,0-.5.5V2.045h-.5a.5.5,0,0,0,0,1H24V6h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v.5a.5.5,0,0,0,.5.5h10A3.5,3.5,0,0,0,38,12.5V10h.5a.5.5,0,0,0,.5-.5v-3A.5.5,0,0,0,38.5,6ZM38,9H37a1,1,0,0,1,0-2h1Zm-1,3.5A2.5,2.5,0,0,1,34.5,15h-9a.5.5,0,0,0,0-1H25V11h.5a.5.5,0,0,0,0-1H25V7h.5a.5.5,0,0,0,0-1H25V3.045h.545a.5.5,0,0,0,0-1H25V1h9.5A2.5,2.5,0,0,1,37,3.5V6a2,2,0,0,0,0,4ZM31.5,8h-1A3.5,3.5,0,0,0,27,11.5,1.5,1.5,0,0,0,28.5,13h5A1.5,1.5,0,0,0,35,11.5,3.5,3.5,0,0,0,31.5,8Zm2,4h-5a.5.5,0,0,1-.5-.5A2.5,2.5,0,0,1,30.5,9h1A2.5,2.5,0,0,1,34,11.5.5.5,0,0,1,33.5,12Z" transform="translate(-23)"></path> </g></svg>
                  <span>Contact List</span>
                </Link>
                  )
                }
                
                {canManageUsers && canShowServiceAccessGatedLinks && !isB2C && isSidebarLinkEnabled(orgSidebarLinks, "link6") && (
                  <Link
                    className={`nav-link ${
                      location.pathname === "/manage-users" ? "active" : ""
                    }`}
                    to="/manage-users"
                    data-tooltip-id="TooltipUserManagement"
                    data-tooltip-content="User Management"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>User Management</span>
                  </Link>
                )}
                
                {canAccessDeveloperTools && !isB2C && isSidebarLinkEnabled(orgSidebarLinks, "link7") && (
                  <Link
                    className={`nav-link ${
                      location.pathname === "/developer-access" ? "active" : ""
                    }`}
                    to="/developer-access"
                    data-tooltip-id="TooltipDeveloperAccess"
                    data-tooltip-content="Developer Access"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 8L3 11.6923L7 16M17 8L21 11.6923L17 16M14 4L10 20" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>Developer Access</span>
                  </Link>
                )}

                {!isB2C && isSidebarLinkEnabled(orgSidebarLinks, "link8") && (
                <Link
                  className={`nav-link ${
                    location.pathname === "/settings" ? "active" : ""
                  }`}
                  to="/settings"
                  // title="Features"
                  data-tooltip-id="TooltipFeatures"
                  data-tooltip-content="Features"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4.9996 7V11M9.9996 2V6M17.9996 16V20M2.9996 9H6.9996M7.9996 4H11.9996M15.9996 18H19.9996M13.9996 7L16.828 9.82843M19.5162 3.74612L20.0819 4.3118C20.4779 4.70782 20.6759 4.90583 20.7501 5.13416C20.8154 5.335 20.8154 5.55135 20.7501 5.75219C20.6759 5.98052 20.4779 6.17853 20.0819 6.57454L6.52508 20.1314C6.12906 20.5274 5.93105 20.7254 5.70272 20.7996C5.50188 20.8649 5.28553 20.8649 5.08469 20.7996C4.85636 20.7254 4.65835 20.5274 4.26234 20.1314L3.69665 19.5657C3.30063 19.1697 3.10262 18.9717 3.02844 18.7433C2.96318 18.5425 2.96318 18.3262 3.02844 18.1253C3.10262 17.897 3.30063 17.699 3.69665 17.303L17.2535 3.74612C17.6495 3.3501 17.8475 3.15209 18.0758 3.0779C18.2767 3.01265 18.493 3.01265 18.6939 3.0779C18.9222 3.15209 19.1202 3.3501 19.5162 3.74612Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                  <span>Settings</span>
                </Link>
                )}
                {canShowServiceAccessGatedLinks && !isB2C && isSidebarLinkEnabled(orgSidebarLinks, "link9") && (
                <Link
                  className={`nav-link ${
                    location.pathname === "/external-templates" ? "active" : ""
                  }`}
                  to="/external-templates"
                  data-tooltip-id="TooltipExternalTemplates"
                  data-tooltip-content="External Templates"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                  <span>External Templates</span>
                </Link>
                )}

                {canShowServiceAccessGatedLinks && !isB2C && isSidebarLinkEnabled(orgSidebarLinks, "link10") && (
                <Link
                  className={`nav-link ${
                    location.pathname === "/integrations" ? "active" : ""
                  }`}
                  to="/integrations"
                  data-tooltip-id="TooltipIntegrations"
                  data-tooltip-content="Integrations"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8.5 14.5L4.5 18.5M4.5 18.5L2 21M4.5 18.5L2 16M4.5 18.5L7 21M15.5 9.5L19.5 5.5M19.5 5.5L22 3M19.5 5.5L22 8M19.5 5.5L17 3M14.5 14.5L9.5 9.5M9.5 9.5L8.5 6L3 3L6 8.5L9.5 9.5ZM14.5 14.5L18 15.5L21 21L15.5 18L14.5 14.5Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                  <span>Integrations</span>
                </Link>
                )}


              </ul>
            </div>
            </div>
            <div className="menuSidebarBottom">
              <div
                className="userProfile termsLink"
                onClick={() => setShowTermsModal(true)}
                data-tooltip-id="TooltipTerms"
                data-tooltip-content="Terms & Conditions"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M9 17H15M9 13H15M9 9H10M13 3H8.2C7.0799 3 6.51984 3 6.09202 3.21799C5.71569 3.40973 5.40973 3.71569 5.21799 4.09202C5 4.51984 5 5.0799 5 6.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V9M13 3L19 9M13 3V7.4C13 7.96005 13 8.24008 13.109 8.45399C13.2049 8.64215 13.3578 8.79513 13.546 8.89101C13.7599 9 14.0399 9 14.6 9H19" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                <span>Terms</span>
              </div>

              {!isB2C && (
              <div className="themeToggle">
                
                <Within
                  toggled={isDarkMode}
                  onToggle={(value) => {
                    setUserThemePreference(value ? "dark" : "light");
                    if (typeof window !== "undefined") {
                      window.localStorage.setItem(themeStorageKey, value ? "dark" : "light");
                    }
                    setIsDarkMode(value);
                  }}
                  duration={750}
                />
                <span>{isDarkMode ? "Dev" : "All"}</span>
              </div>
              )}

              
              <div
                className={"userProfile dashboardBranding mb-3 mb-md-0 position-relative " + (showOrgSettingPopup ? "active" : "")}
                onClick={(e) => setShowOrgSettingPopup(!showOrgSettingPopup)}
              >
                <div className="orgInitial" aria-hidden> {orgInitial} </div>

                <OutsideClickHandler onOutsideClick={() => setShowOrgSettingPopup(false)}>
                  <Popup
                    show={showOrgSettingPopup}
                    onClose={() => setShowOrgSettingPopup(false)}
                    header="Account"
                  >
                    <div className="orgAccountInfo">
                      <div className="infoRow">
                        <div className="infoLabel">Organization</div>
                        <div className="infoValue">{organizationName}</div>
                      </div>
                      <div className="infoRow">
                        <div className="infoLabel">Signed in as</div>
                        <div className="infoValue">{userInfo?.name || "—"}</div>
                        <div className="infoSubValue">{userInfo?.email || ""}</div>
                      </div>
                    </div>
                  </Popup>
                </OutsideClickHandler>
              </div>

              <div className="userProfile" onClick={(e) => setProfileDropdownShow(true)}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18 18.7023C18 15.6706 14.5 15 12 15C9.5 15 6 15.6706 6 18.7023M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12ZM15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                <span>My profile</span>
              </div>
              
              {
                profileDropdownShow ?
                <OutsideClickHandler onOutsideClick={(e) => setProfileDropdownShow(false)}>
                  <div className="profileDropdown">
                    <div className="profileDropdownUser">
                      <div className="profileDropdownUserName">{userInfo?.name || "—"}</div>
                      {userInfo?.email && (
                        <div className="profileDropdownUserEmail">{userInfo.email}</div>
                      )}
                    </div>
                    <div className="profileDropdownDivider" />
                    <div className="logOut profileDropdownItem" onClick={_logout}>
                      <i className="fa fa-power-off"></i> <span>Logout</span>
                    </div>
                  </div>
                </OutsideClickHandler> : ""
              }
            </div>
          </div>
        </div>
      </OutsideClickHandler>

      {/* Top Bar */}
      <div className="topbar">
        <div className="left">
          {/* <div className="userProfile" onClick={(e) => setProfileDropdownShow(true)}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18 18.7023C18 15.6706 14.5 15 12 15C9.5 15 6 15.6706 6 18.7023M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12ZM15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </div> */}

          <div className="toggleSidebar" onClick={(e) => setToggleSidebar(true)}>
            <svg viewBox="-0.5 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M19 3.32001H16C14.8954 3.32001 14 4.21544 14 5.32001V8.32001C14 9.42458 14.8954 10.32 16 10.32H19C20.1046 10.32 21 9.42458 21 8.32001V5.32001C21 4.21544 20.1046 3.32001 19 3.32001Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M8 3.32001H5C3.89543 3.32001 3 4.21544 3 5.32001V8.32001C3 9.42458 3.89543 10.32 5 10.32H8C9.10457 10.32 10 9.42458 10 8.32001V5.32001C10 4.21544 9.10457 3.32001 8 3.32001Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M19 14.32H16C14.8954 14.32 14 15.2154 14 16.32V19.32C14 20.4246 14.8954 21.32 16 21.32H19C20.1046 21.32 21 20.4246 21 19.32V16.32C21 15.2154 20.1046 14.32 19 14.32Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M8 14.32H5C3.89543 14.32 3 15.2154 3 16.32V19.32C3 20.4246 3.89543 21.32 5 21.32H8C9.10457 21.32 10 20.4246 10 19.32V16.32C10 15.2154 9.10457 14.32 8 14.32Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </div>
        </div>

        
        <div className="right">
          <div className="orgName">
            <span>{organizationName}</span>
          </div>
        </div>
      </div>

      {/* Subtle presence pill — minimal user/org indicator */}
      {(userInfo?.name || userInfo?.email || orgDetails?.name) && (
        <OutsideClickHandler onOutsideClick={closePresence}>
          <div className={"presencePill-wrapper " + (presenceOpen ? "open" : "")}>
            <button
              type="button"
              className="presencePill"
              aria-label="Signed-in context"
              aria-expanded={presenceOpen}
              onClick={() => setPresenceOpen((v) => !v)}
            >
              <span className="presencePill-dot" aria-hidden>
                {(userInfo?.name?.trim()?.[0] || userInfo?.email?.trim()?.[0] || "?").toUpperCase()}
              </span>
              {userInfo?.name && (
                <span className="presencePill-user">{userInfo.name.split(" ")[0]}</span>
              )}
              {userInfo?.name && orgDetails?.name && (
                <span className="presencePill-sep" aria-hidden>·</span>
              )}
              {orgDetails?.name && (
                <span className="presencePill-org">{orgDetails.name}</span>
              )}
            </button>

            {presenceOpen && (
              <div className="presencePanel" role="dialog" aria-label="Account details">
                <div className="presencePanel-row">
                  <div className="presencePanel-label">Signed in as</div>
                  <div className="presencePanel-value">{userInfo?.name || "—"}</div>
                  {userInfo?.email && (
                    <div className="presencePanel-sub">{userInfo.email}</div>
                  )}
                </div>
                <div className="presencePanel-row">
                  <div className="presencePanel-label">Organization</div>
                  <div className="presencePanel-value">{organizationName}</div>
                </div>

                <div className="presencePanel-actions">
                  {logoutConfirming ? (
                    <>
                      <button
                        type="button"
                        className="presencePanel-btn ghost"
                        onClick={() => setLogoutConfirming(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="presencePanel-btn danger"
                        onClick={(e) => {
                          closePresence();
                          _logout(e);
                        }}
                      >
                        Confirm logout
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="presencePanel-btn danger-outline"
                      onClick={() => setLogoutConfirming(true)}
                    >
                      <i className="fa fa-power-off"></i>
                      <span>Logout</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </OutsideClickHandler>
      )}

      {/* Page Content */}
      <div className="page-wrapper">
        <div className="page-content-tab">
          <div className="container h-100">{children}</div>
        </div>
      </div>
      <QRViewer />


      {showTermsModal && (
        <TermsModal
          isVisible={showTermsModal}
          isAccepted={isTermsAccepted}
          onAccept={handleAcceptTerms}
          onClose={() => setShowTermsModal(false)}
          isAccepting={isAcceptingTerms}
        />
      )}

      {/* MOBILE BOTTOM BAR */}
      <div className="mobile-bottom-bar">
        {canShowServiceAccessGatedLinks ? (
          <div className="item">
            <Link
              className={`itemLink ${
                location.pathname === "/connect-contacts" ? "active" : ""
              }`}
              to="/connect-contacts"
              // title="Connect Contacts"
              data-tooltip-id="TooltipConnectContacts"
              data-tooltip-content="Connect Contacts"
            >
              <svg fill="#000000" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 465.00 465.00" stroke="#000000" stroke-width="10.695"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M117.5,173c34.462,0,62.5-28.037,62.5-62.5S151.962,48,117.5,48S55,76.037,55,110.5S83.038,173,117.5,173z M117.5,63 c26.191,0,47.5,21.309,47.5,47.5S143.691,158,117.5,158S70,136.691,70,110.5S91.309,63,117.5,63z"></path> <path d="M282.5,173c34.462,0,62.5-28.037,62.5-62.5S316.962,48,282.5,48S220,76.037,220,110.5S248.038,173,282.5,173z M282.5,63 c26.191,0,47.5,21.309,47.5,47.5S308.691,158,282.5,158S235,136.691,235,110.5S256.309,63,282.5,63z"></path> <path d="M363.656,256.882c1.478,1.912,3.697,2.915,5.94,2.914c1.601,0,3.215-0.511,4.581-1.565 c3.278-2.533,3.881-7.243,1.349-10.521C353.077,218.66,319.17,202,282.5,202c-23.545,0-46.264,6.94-65.7,20.071 c-3.433,2.319-4.335,6.981-2.017,10.413c2.32,3.434,6.982,4.336,10.413,2.017C242.144,223.052,261.959,217,282.5,217 C314.489,217,344.07,231.536,363.656,256.882z"></path> <path d="M117.5,202C52.71,202,0,254.71,0,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C15,262.981,60.981,217,117.5,217 S220,262.981,220,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C235,254.71,182.29,202,117.5,202z"></path> <path d="M392.5,272c-39.977,0-72.5,32.523-72.5,72.5s32.523,72.5,72.5,72.5s72.5-32.523,72.5-72.5S432.477,272,392.5,272z M392.5,402c-31.706,0-57.5-25.794-57.5-57.5s25.794-57.5,57.5-57.5s57.5,25.794,57.5,57.5S424.206,402,392.5,402z"></path> <path d="M417.5,337H400v-17.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5V337h-17.5c-4.142,0-7.5,3.357-7.5,7.5 s3.358,7.5,7.5,7.5H385v17.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5V352h17.5c4.142,0,7.5-3.357,7.5-7.5 S421.642,337,417.5,337z"></path> </g> </g></svg>
            </Link>
          </div>
        ) : null}

        {canShowServiceAccessGatedLinks ? (
          <div className="item">
            <Link
              className={`itemLink ${
                location.pathname === "/manage-groups" ? "active" : ""
              }`}
              to="/manage-groups"
              data-tooltip-id="TooltipManageGroups"
              data-tooltip-content="Manage Groups"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M17 20C17 18.3431 14.7614 17 12 17C9.23858 17 7 18.3431 7 20M21 17.0004C21 15.7702 19.7659 14.7129 18 14.25M3 17.0004C3 15.7702 4.2341 14.7129 6 14.25M18 10.2361C18.6137 9.68679 19 8.8885 19 8C19 6.34315 17.6569 5 16 5C15.2316 5 14.5308 5.28885 14 5.76389M6 10.2361C5.38625 9.68679 5 8.8885 5 8C5 6.34315 6.34315 5 8 5C8.76835 5 9.46924 5.28885 10 5.76389M12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11C15 12.6569 13.6569 14 12 14Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
            </Link>
          </div>
        ) : null}

        {canShowServiceAccessGatedLinks && !isB2C ? (
          <div className="item">
            <Link
              className={`itemLink ${
                location.pathname === "/transcript-chats" ? "active" : ""
              }`}
              to="/transcript-chats"
              // title="Messages"
              data-tooltip-id="TooltipTranscripts"
              data-tooltip-content="Transcripts"
            >
              <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M0 0h48v48H0z" fill="none"></path> <g id="Shopicon"> <path d="M4,44l8-8h20c2.2,0,4-1.8,4-4v-4l8,8V8c0-2.2-1.8-4-4-4H16c-2.2,0-4,1.8-4,8v4H8c-2.2,0-4,1.8-4,4V44z M16,8h24v18.343 L37.657,24H16V8z M8,16h4v8c0,2.2,1.8,4,4,4h16v4H10.343L8,34.343V16z"></path> </g> </g></svg>
            </Link>
          </div>
        ) : null}
          


        {canShowServiceAccessGatedLinks && !isB2C ? (
          <div className="item">
            <Link
              className={`itemLink ${
                location.pathname === "/whatsapp-chats" ? "active" : ""
              }`}
              to="/whatsapp-chats"
              // title="Messages"
              data-tooltip-id="TooltipMessages"
              data-tooltip-content="Messages"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 9H17M7 13H12M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            </Link>
          </div>
        ) : null}


        {canShowServiceAccessGatedLinks && !isB2C ? (
          <div className="item">
            <Link
              className={`itemLink ${
                location.pathname === "/whatsapp-groups" ? "active" : ""
              }`}
              to="/whatsapp-groups"
              data-tooltip-id="TooltipGroupMessaging"
              data-tooltip-content="Group messaging"
            >
              <svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg"> <circle cx="13" cy="6.5" r="5" stroke="black" stroke-width="3"/> <circle cx="13" cy="30.5" r="5" stroke="black" stroke-width="3"/> <circle cx="36.5" cy="6.5" r="5" stroke="black" stroke-width="3"/> <circle cx="36.5" cy="30.5" r="5" stroke="black" stroke-width="3"/> <path d="M24 24C24 17.9249 19.0751 13 13 13C6.92487 13 2 17.9249 2 24" stroke="black" stroke-width="3"/> <path d="M24 48C24 41.9249 19.0751 37 13 37C6.92487 37 2 41.9249 2 48" stroke="black" stroke-width="3"/> <path d="M47.5 24C47.5 17.9249 42.5751 13 36.5 13C30.4249 13 25.5 17.9249 25.5 24" stroke="black" stroke-width="3"/> <path d="M47.5 48C47.5 41.9249 42.5751 37 36.5 37C30.4249 37 25.5 41.9249 25.5 48" stroke="black" stroke-width="3"/> </svg>
            </Link>
          </div>
        ) : null}


        {!isB2C && (
        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/settings" ? "active" : ""
            }`}
            to="/settings"
            // title="Features"
            data-tooltip-id="TooltipFeatures"
            data-tooltip-content="Features"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4.9996 7V11M9.9996 2V6M17.9996 16V20M2.9996 9H6.9996M7.9996 4H11.9996M15.9996 18H19.9996M13.9996 7L16.828 9.82843M19.5162 3.74612L20.0819 4.3118C20.4779 4.70782 20.6759 4.90583 20.7501 5.13416C20.8154 5.335 20.8154 5.55135 20.7501 5.75219C20.6759 5.98052 20.4779 6.17853 20.0819 6.57454L6.52508 20.1314C6.12906 20.5274 5.93105 20.7254 5.70272 20.7996C5.50188 20.8649 5.28553 20.8649 5.08469 20.7996C4.85636 20.7254 4.65835 20.5274 4.26234 20.1314L3.69665 19.5657C3.30063 19.1697 3.10262 18.9717 3.02844 18.7433C2.96318 18.5425 2.96318 18.3262 3.02844 18.1253C3.10262 17.897 3.30063 17.699 3.69665 17.303L17.2535 3.74612C17.6495 3.3501 17.8475 3.15209 18.0758 3.0779C18.2767 3.01265 18.493 3.01265 18.6939 3.0779C18.9222 3.15209 19.1202 3.3501 19.5162 3.74612Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </Link>
        </div>
        )}


        {/* <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/external-templates" ? "active" : ""
            }`}
            to="/external-templates"
            data-tooltip-id="TooltipExternalTemplates"
            data-tooltip-content="External Templates"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </Link>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardLayout;
